// Ported from design/project/App40-Withdraw.dc.html and App40c-WithdrawConfirm.dc.html, then wired to the balance, the
// connected wallet and the payout queue. WITHDRAW opens a confirmation; only its Confirm sends the money.
import { useEffect, useState } from "react";
import { ApiError, api } from "../api.js";
import { useGame } from "../game.js";
import { cleanAmount, holdUntil, shortAddress, useMoney, walletName } from "../money.js";
import { go, setBackOverride } from "../router.js";
import { AFTER_CONNECT } from "./App39-Deposit.js";
import { Line, Notice, SheetBackdrop, SheetTitle } from "./money-parts.js";

const PROBLEMS: Record<string, string> = {
  low_balance: "Not enough balance for that.",
  play_money: "Play money can't be withdrawn.",
  network: "No connection. Check your internet and try again.",
};

export function App40_Withdraw() {
  const { me, refresh } = useGame();
  const { view, reload } = useMoney();
  const available = me?.balance.available ?? view?.withdraw.available ?? null;
  // null until the player types: the field starts at the whole balance, and an emptied field stays empty.
  const [amount, setAmount] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** The confirmation is open, with the id this withdrawal is sent under: a second tap or a retry is the same one. */
  const [confirming, setConfirming] = useState<string | null>(null);
  // Back closes the confirmation first, keeping the amount; a second Back leaves the screen.
  useEffect(() => {
    if (!confirming) return;
    setBackOverride(() => setConfirming(null));
    return () => setBackOverride(null);
  }, [confirming]);
  const shown = amount ?? available ?? "";
  const value = cleanAmount(shown);
  const fee = Number(view?.withdraw.fee ?? "0.10");
  const receive = value ? Math.max(0, Number(value) - fee).toFixed(2) : "0.00";

  if (view && holdUntil(view)) {
    go("App75", true);
    return null;
  }

  /** WITHDRAW: checks the amount and asks to confirm. Nothing is sent yet. */
  const review = () => {
    if (!view?.wallet) {
      sessionStorage.setItem(AFTER_CONNECT, "App40");
      go("App04");
      return;
    }
    if (!value) return setProblem(`The smallest withdrawal is ${view.withdraw.min} USDT.`);
    if (available && Number(value) > Number(available)) return setProblem(PROBLEMS.low_balance!);
    setProblem(null);
    setConfirming(crypto.randomUUID());
  };

  /** Confirm: sends it. */
  const withdraw = async () => {
    if (!confirming || busy || !value) return;
    setBusy(true);
    setProblem(null);
    try {
      // One id per confirmation: a retried request after a dropped connection is the same withdrawal, not a second one.
      const requestId = confirming;
      const r = await api<{ id: string }>("/v1/withdraw", { method: "POST", body: { amount: value, requestId } });
      sessionStorage.setItem("dd:withdrawal", r.id);
      void refresh();
      go("App71", true);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "network";
      if (code === "wallet_hold") return void reload();
      setProblem((e instanceof ApiError && typeof e.detail.message === "string" ? e.detail.message : null) ?? PROBLEMS[code] ?? "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="screen">
  <SheetBackdrop />
  <div className="sheet g squeeze" style={{ background: "rgba(18,22,29,0.96)", borderBottom: "0" }}>
    <SheetTitle>Withdraw</SheetTitle>

    <label style={{ marginTop: "16px", padding: "18px", borderRadius: "20px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
      <span className="lbl">YOU WITHDRAW</span>
      <span className="mono" style={{ fontWeight: "800", fontSize: "44px", lineHeight: "50px", display: "flex", alignItems: "baseline", gap: "8px" }}>
        <input aria-label="Amount in USDT" inputMode="decimal" value={shown} onChange={(e) => setAmount(e.target.value.slice(0, 10))}
          style={{ width: `${Math.max(2, shown.length) + 0.5}ch`, maxWidth: "7ch", background: "transparent", border: "0", outline: "none", color: "#FFFFFF", font: "inherit", textAlign: "right", padding: "0" }} />
        <span style={{ fontSize: "18px" }} className="muted">USDT</span>
      </span>
      <span className="muted" style={{ fontSize: "12px" }}>Available <span className="mono" style={{ color: "#FFFFFF" }}>{available ?? "..."}</span> · <button onClick={(e) => { e.preventDefault(); setAmount(available ?? ""); }} className="lime" style={{ fontWeight: "600", background: "none", border: "0", padding: "0", font: "inherit", cursor: "pointer" }}>MAX</button></span>
    </label>

    {me && Number(me.balance.inBattle) > 0 && (
      <div style={{ marginTop: "12px", display: "flex", gap: "10px", alignItems: "flex-start", padding: "12px 14px", borderRadius: "16px", background: "rgba(255,255,255,0.04)" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2.2" strokeLinecap="round" style={{ flex: "none" }} aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"></rect><path d="M8 11V7a4 4 0 0 1 8 0v4"></path></svg>
        <span className="muted" style={{ fontSize: "12px", lineHeight: "17px" }}><span className="mono" style={{ color: "#FFFFFF" }}>{me.balance.inBattle}</span> more is in a live battle and becomes available when it settles.</span>
      </div>
    )}

    <div className="g" style={{ marginTop: "12px", borderRadius: "18px", padding: "4px 0" }}>
      <Line first label="To">{view?.wallet ? <>{walletName(view.wallet.appName)} <span className="mono muted">{shortAddress(view.wallet.address)}</span></> : <span className="muted">Connect a wallet first</span>}</Line>
      <Line label="Minimum withdrawal"><span className="mono">{view?.withdraw.min ?? "5.00"} USDT</span></Line>
      <Line label="Network fee"><span className="mono">{view?.withdraw.fee ?? "0.10"} USDT</span></Line>
      <Line label="You receive"><span className="mono lime" style={{ fontSize: "14px", fontWeight: "800" }}>{receive} USDT</span></Line>
      <Line label="Arrives">about 1 minute</Line>
    </div>

    <p className="muted" style={{ margin: "12px 2px 0", fontSize: "11px", lineHeight: "16px" }}>Withdrawals only go to the wallet you connected. Changing it pauses withdrawals for 24 hours.</p>

    {problem && !confirming && <Notice>{problem}</Notice>}
    <button onClick={review} disabled={busy} className="btnP" style={{ marginTop: "14px", border: "0", cursor: "pointer", opacity: busy ? 0.6 : 1 }}>{!view?.wallet ? "CONNECT WALLET" : busy ? "SENDING..." : !value ? "WITHDRAW" : `WITHDRAW ${value}${value.length > 6 ? "" : " USDT"}`}</button>
  </div>
  {confirming && view?.wallet && (
    <div role="dialog" aria-modal="true" aria-label="Confirm withdrawal" onClick={() => !busy && setConfirming(null)} style={{ position: "fixed", inset: "0", zIndex: 30, background: "rgba(11,14,20,0.82)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ boxSizing: "border-box", width: "100%", maxWidth: "480px", alignSelf: "center", maxHeight: "calc(100% - var(--tg-safe-area-inset-top, 47px) - var(--tg-content-safe-area-inset-top, 47px))", overflowY: "auto", padding: "18px 20px calc(var(--tg-safe-area-inset-bottom, 34px) + var(--tg-content-safe-area-inset-bottom, 0px) + 12px)", borderRadius: "26px 26px 0 0", background: "#161B22", borderTop: "3px solid #CCFF00", display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
        <div style={{ alignSelf: "center", width: "40px", height: "4px", borderRadius: "2px", background: "#30363D", marginBottom: "14px", flex: "none" }}></div>
        <span style={{ alignSelf: "center", fontFamily: "Unbounded, sans-serif", fontWeight: "900", fontSize: "13px", letterSpacing: "0.08em" }}>CONFIRM WITHDRAWAL</span>
        <div style={{ marginTop: "12px", alignSelf: "center", display: "flex", alignItems: "baseline", gap: "8px" }}><span className="mono" style={{ fontWeight: "800", fontSize: "44px", lineHeight: "50px" }}>{Number(value).toFixed(2)}</span><span className="muted" style={{ fontSize: "16px" }}>USDT</span></div>
        <div style={{ marginTop: "14px", borderRadius: "18px", background: "#0B0E14", border: "1px solid rgba(255,255,255,0.08)" }}>
          <Line first label="To">{walletName(view.wallet.appName)} <span className="mono muted">{shortAddress(view.wallet.address)}</span></Line>
          <Line label="Network fee"><span className="mono">{view.withdraw.fee} USDT</span></Line>
          <Line label="You receive"><span className="mono lime" style={{ fontSize: "15px", fontWeight: "800" }}>{receive} USDT</span></Line>
          <Line label="Arrives">about 1 minute</Line>
        </div>
        <div style={{ marginTop: "12px", display: "flex", gap: "10px", alignItems: "flex-start", padding: "10px 12px", borderRadius: "14px", background: "rgba(255,51,102,0.08)", border: "1px solid rgba(255,51,102,0.3)" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF5C85" strokeWidth="2.2" strokeLinecap="round" style={{ flex: "none", marginTop: "1px" }} aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 8v5M12 16v.5"></path></svg>
          <span style={{ fontSize: "12px", lineHeight: "17px", color: "#C9D1D9" }}>Check the wallet. Once sent, a withdrawal can't be cancelled or brought back.</span>
        </div>
        {problem && <Notice>{problem}</Notice>}
        <button onClick={() => void withdraw()} disabled={busy} className="btnP" style={{ marginTop: "16px", border: "0", cursor: "pointer", opacity: busy ? 0.6 : 1, flex: "none", fontSize: "14px", whiteSpace: "nowrap" }}>{busy ? "SENDING..." : "CONFIRM WITHDRAWAL"}</button>
        <button onClick={() => setConfirming(null)} disabled={busy} className="btnS" style={{ marginTop: "10px", flex: "none" }}>Cancel</button>
      </div>
    </div>
  )}
</div>
    </>
  );
}
